import { defineComponent as _defineComponent } from 'vue'
import { TradeDestination } from './forms/tradeDestination';
import { TradeFundsDestination } from '../models/trade';
import { Observable } from 'rxjs';
import UpdatedDate from 'ah-common-lib/src/common/components/time/UpdatedDate.vue';
import TradePriceMarginInfoView from './info/TradePriceMarginInfoView.vue';
import TradeBeneficiaryInfo from './info/TradeBeneficiaryInfo.vue';
import CostTransparencyDetailsInfo from './info/CostTransparencyDetailsInfo.vue';
import TradeDetailsInfo from './info/TradeDetailsInfo.vue';
import { pricingRequest, QuotePriceRequest } from '../requests/pricingRequest';
import { getChildModel, makeFormModel, setState } from 'ah-common-lib/src/form/helpers';
import { checkboxField } from 'ah-common-lib/src/form/models';
import {
  isPriceExpired,
  HedgingInstruments,
  getBuyCcy,
  AuthorityType,
  TradeFundingDetails,
  AmountType,
  FeatureFlag,
  FeatureFlagTreatment,
} from 'ah-api-gateways';
import WalletTransferText from 'ah-wallets/src/components/WalletTransferText.vue';
import { reactive, onBeforeUnmount, computed, watch, ref } from 'vue';
import { useAhTradesState } from '..';
import { FormDefinition } from 'ah-common-lib/src/form/interfaces';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { isEmpty } from 'lodash';
import { useAggregatedPaymentLimitChecker } from '../composables/aggregatedPaymentLimitChecker';
import AggregatedLimitErrorMessage from '../../../ah-payments/src/components/AggregatedLimitErrorMessage.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradeConfirmation',
  props: {
    tradeDestination: null,
    tradePrice: null,
    tradeFundingDetails: null,
    loading: { type: [Boolean, String], default: false },
    narrow: { type: [Boolean, String], default: false },
    syncTradeFunds: { type: [Boolean, String], default: false },
    costTransparency: { type: [String, Boolean], default: false },
    costTransparencyCurrency: null
  },
  emits: ["outdated-price", "update:tradePrice", "trade-confirmed", "revert-to-form", "update:costTransparencyCurrency"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'outdated-price'): void;
  (e: 'update:tradePrice', value: QuotePriceRequest): void;
  (e: 'trade-confirmed'): void;
  (e: 'revert-to-form'): void;
  (e: 'update:costTransparencyCurrency', value: AmountType): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { tradeDestination: TradeDestination, tradePrice: QuotePriceRequest, tradeFundingDetails?: Partial<TradeFundingDetails>, loading: boolean | string, narrow: boolean | string, syncTradeFunds: boolean | string, costTransparency: string | boolean, costTransparencyCurrency: AmountType };





const tradeState = useAhTradesState();

const onBehalfOfClient = useOnBehalfOf();

const requestManager = useRequestManager();

const displayCostTransparency = ref<boolean>(false);

const showCostTransparency = computed(() => props.costTransparency !== false);

function loadFeatureFlag() {
  const payload = {
    featureFlag: FeatureFlag.PRICING_SHOW_CLIENT_COSTS,
    oboClientId: onBehalfOfClient.value?.id,
    force: true,
  };

  tradeState.store
    .useFeatureFlagStore()
    .loadFeatureFlag(payload)
    .then(
      (featureFlagResponse) =>
        (displayCostTransparency.value = featureFlagResponse?.treatment === FeatureFlagTreatment.ON)
    );
}

// We skip feature flags checking in x-office to hide cost transparency in trading obo
if (!tradeState.store.useAuthStore().isAHUser && showCostTransparency.value) {
  loadFeatureFlag();
}

const state = reactive<{
  areIMLimitOrCollateralProfileInvalid: boolean;
  needsPriceRefresh: boolean;
  sufficientCollateral: boolean;
  recheckRefreshTimeout: number | null;
}>({
  areIMLimitOrCollateralProfileInvalid: false,
  needsPriceRefresh: false,
  sufficientCollateral: false,
  recheckRefreshTimeout: null,
});

const clientId = computed(() => {
  return onBehalfOfClient.value?.id ?? tradeState.store.useAuthStore().loggedInIdentity?.client?.id ?? '';
});

const oboClientId = computed(() => onBehalfOfClient.value?.id);

const { aggregatedLimitForCurrency, aggregatedPaymentReviewRoute, isAggregatedLimitReached, loadAggregatedLimit } =
  useAggregatedPaymentLimitChecker({
    requestManager: requestManager.manager,
    services: tradeState.services,
    data: {
      clientId,
      oboClientId,
      paymentCurrency: computed(() => props.tradePrice.tradeDetails.buyCurrency),
      paymentAmount: computed(() => props.tradePrice.response?.ccy1.clientAmount),
    },
  });

const forwardConfirmForm = reactive<FormDefinition>({
  form: makeFormModel({
    fieldType: 'form',
    name: 'forwardConfirmForm',
    fields: [
      checkboxField(
        'confirmed',
        'I confirm that the purpose of this trade is to facilitate a payment for goods and services or direct investment.',
        false,
        {
          required: false,
          errorMessages: {
            mustAccept: 'Must confirm this to continue',
          },
        }
      ),
    ],
  }),
  validation: null,
});

onBeforeUnmount(() => {
  if (state.recheckRefreshTimeout) {
    clearTimeout(state.recheckRefreshTimeout);
  }
});

const canSeeCollateral = computed(() => tradeState.store.useAuthStore().hasAuthorities(AuthorityType.VIEW_COLLATERALS));

const isNarrow = computed(() => props.narrow !== false);

const isLoading = computed(() => props.loading !== false);

const isSyncTradeFunds = computed(() => props.syncTradeFunds !== false);

const validFunding = computed(() => {
  if (!props.tradeFundingDetails || isEmpty(props.tradeFundingDetails)) return true;
  return props.tradeFundingDetails.valid;
});

const isAForward = computed(() => {
  return [
    HedgingInstruments.FX_FORWARD,
    HedgingInstruments.WINDOW_FORWARD,
    HedgingInstruments.FLEXIBLE_FORWARD,
  ].includes(props.tradePrice.response?.hedgingProduct!);
});

const isUnconfirmedForward = computed(() => isAForward.value && !forwardConfirmForm.form.confirmed);

const buyCcy = computed(() => {
  if (props.tradePrice && props.tradePrice.response) {
    return getBuyCcy(props.tradePrice.response);
  }
  return undefined;
});

function recheckRefresh() {
  state.needsPriceRefresh = !!(
    props.tradePrice.response && isPriceExpired(props.tradePrice.response, props.tradePrice.responseTimeOffset)
  );
  state.recheckRefreshTimeout = window.setTimeout(() => recheckRefresh(), 1000);
}

recheckRefresh();

function reloadPrices() {
  if (props.loading) {
    return;
  }
  const cutoffDate = new Date(props.tradePrice.tradeDetails.timeFrame?.cutoffDateTime ?? NaN);
  if (cutoffDate < new Date()) {
    emit('outdated-price');
    return;
  }
  loadAggregatedLimit();
  return requestManager.manager.sameOrCancelAndNew('reloadPrices', pricingRequestObs()).subscribe((price) => {
    emit('update:tradePrice', price);
  });
}

function pricingRequestObs(): Observable<QuotePriceRequest> {
  return pricingRequest(props.tradePrice.tradeDetails, clientId.value, tradeState.services.pricingEngine);
}

function confirmTrade() {
  emit('trade-confirmed');
}

function revertToForm() {
  emit('revert-to-form');
}

watch(
  validFunding,
  () => {
    setState(getChildModel(forwardConfirmForm.form, 'confirmed')!, 'readonly', !validFunding.value);
    if (!validFunding.value) {
      forwardConfirmForm.form.confirmed = false;
      forwardConfirmForm.validation?.$reset();
    }
  },
  { immediate: true }
);

return { __sfc: true,props, emit, tradeState, onBehalfOfClient, requestManager, displayCostTransparency, showCostTransparency, loadFeatureFlag, state, clientId, oboClientId, aggregatedLimitForCurrency, aggregatedPaymentReviewRoute, isAggregatedLimitReached, loadAggregatedLimit, forwardConfirmForm, canSeeCollateral, isNarrow, isLoading, isSyncTradeFunds, validFunding, isAForward, isUnconfirmedForward, buyCcy, recheckRefresh, reloadPrices, pricingRequestObs, confirmTrade, revertToForm, TradeFundsDestination, UpdatedDate, TradePriceMarginInfoView, TradeBeneficiaryInfo, CostTransparencyDetailsInfo, TradeDetailsInfo, WalletTransferText, AggregatedLimitErrorMessage }
}

})